"use client"

import {
  Container,
  Stack,
  Box,
  Text,
  Group,
  Badge,
  Anchor,
  Image,
  Grid,
  Divider,
  Avatar,
} from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { getCountryName } from "@forestsync/db"
import {
  TbArrowLeft,
  TbExternalLink,
  TbLeaf,
  TbMapPin,
  TbTree,
} from "react-icons/tb"
import Link from "next/link"
import { PriceHistoryChart } from "./PriceHistoryChart"
import { MarketBuyBox } from "./MarketBuyBox"
import type { MarketSummary, SinkDetail } from "@/lib/queries"

const REGISTRY_URL =
  process.env.NEXT_PUBLIC_REGISTRY_URL ?? "http://localhost:4100"

const getImageByCreditType = (type: string | null, refId: string) => {
  const tStr = type?.toLowerCase() || ""
  let hash = 0
  for (let i = 0; i < refId.length; i++) hash += refId.charCodeAt(i)
  const idx = hash % 3

  if (tStr.includes("biochar")) {
    return ["/images/biochar.png", "/images/biochar1.png", "/images/biochar3.png"][idx]
  }
  if (tStr.includes("afforestation")) {
    return ["/images/aff.png", "/images/aff2.png", "/images/aff3.png"][idx]
  }
  if (tStr.includes("reforestation") || tStr.includes("forest")) {
    return ["/images/reforest.png", "/images/reforest1.png", "/images/reforest2.png"][idx]
  }
  if (tStr.includes("redd")) {
    return ["/images/redd.png", "/images/redd0.png", "/images/redd1.png"][idx]
  }
  if (tStr.includes("soil")) {
    return ["/images/soil.png", "/images/soil2.png", "/images/soil3.png"][idx]
  }
  if (tStr.includes("blue") || tStr.includes("mangrove")) {
    return ["/images/blue1.png", "/images/blue2.png", "/images/blue3.png"][idx]
  }
  if (tStr.includes("renew") || tStr.includes("energy")) {
    return "/images/renew0.png"
  }
  return "/images/reforest.png"
}

interface ProjectMarketDetailViewProps {
  market: MarketSummary
  sink: SinkDetail | null
  bars: Array<{ time: number; value: number }>
  isAuthenticated: boolean
  vintages?: MarketSummary[]
}

export function ProjectMarketDetailView({
  market,
  sink,
  bars,
  isAuthenticated,
  vintages = [],
}: ProjectMarketDetailViewProps) {
  const t = useTranslations("trading")

  const coverImage =
    sink?.galleryUrls?.[0] ??
    getImageByCreditType(market.creditType ?? market.methodology, market.projectRefId)

  const displayName = sink?.name ?? market.projectName

  return (
    <>
      {/* Hero */}
      <Box w="100%" h={320} pos="relative" style={{ overflow: "hidden" }}>
        <Image
          src={coverImage}
          alt={displayName}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          fallbackSrc="https://placehold.co/1600x320?text=Project+Cover"
        />
        <Box
          pos="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)",
          }}
        />
        <Container
          size="lg"
          pos="absolute"
          style={{ bottom: 32, left: 0, right: 0 }}
        >
          <Anchor
            component={Link}
            href="/market"
            c="white"
            size="sm"
            mb="sm"
            display="inline-block"
          >
            <Group gap={4}>
              <TbArrowLeft size={14} />
              {t("backToMarkets")}
            </Group>
          </Anchor>

          <Group gap="md" align="flex-end" wrap="nowrap">
            {sink?.ownerLogoUrl && (
              <Avatar
                src={sink.ownerLogoUrl}
                size={52}
                radius="sm"
                style={{ border: "2px solid rgba(255,255,255,0.3)", flexShrink: 0 }}
              />
            )}
            <Box>
              {market.ownerName && (
                <Text size="sm" c="rgba(255,255,255,0.75)" fw={500} mb={2}>
                  {market.ownerName}
                </Text>
              )}
              <Text fw={800} size="2.2rem" lh={1.1} c="white">
                {displayName}
              </Text>
              <Group gap="xs" mt="xs">
                {market.creditType && (
                  <Badge variant="filled" color="green" size="md">
                    {market.creditType}
                  </Badge>
                )}
                <Badge variant="white" color="dark" size="md">
                  {t("storefront.vintage")} {market.vintage}
                </Badge>
              </Group>
            </Box>
          </Group>
        </Container>
      </Box>

      {/* Stats bar */}
      <Box py="md" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Container size="lg">
          <Group gap="xl" wrap="wrap">
            <Box>
              <Text size="xs" c="dimmed">{t("price")}</Text>
              <Text fw={700} size="lg">
                {market.lastPrice !== null ? `€${market.lastPrice.toFixed(2)}` : "N/A"}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t("change24h")}</Text>
              {market.change24hPct !== null ? (
                <Badge
                  color={market.change24hPct >= 0 ? "green" : "red"}
                  variant="light"
                  size="lg"
                >
                  {market.change24hPct >= 0 ? "+" : ""}
                  {market.change24hPct.toFixed(2)}%
                </Badge>
              ) : (
                <Text fw={600}>N/A</Text>
              )}
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t("bestBid")}</Text>
              <Text fw={600}>
                {market.bestBid !== null ? `€${market.bestBid.toFixed(2)}` : "N/A"}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t("bestAsk")}</Text>
              <Text fw={600}>
                {market.bestAsk !== null ? `€${market.bestAsk.toFixed(2)}` : "N/A"}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t("totalVolume24h")}</Text>
              <Text fw={600}>
                {Math.round(market.totalVolume24h).toLocaleString()} tCO₂e
              </Text>
            </Box>
          </Group>
        </Container>
      </Box>

      <Container size="lg" py={56} pb={80}>
        {/* Main layout: project info left, buy box right */}
        <Grid gutter={48} align="flex-start">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="xl">
              {/* About */}
              {sink?.description && (
                <Box>
                  <Text size="lg" fw={700} mb="sm">
                    {t("storefront.aboutProject")}
                  </Text>
                  <Text size="md" c="dimmed" lh={1.75}>
                    {sink.description}
                  </Text>
                </Box>
              )}

              <Divider />

              {/* Project details */}
              <Box>
                <Text size="lg" fw={700} mb="md">
                  {t("storefront.projectDetails")}
                </Text>
                <Grid gutter="md">
                  {sink?.region && sink?.country && (
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Group wrap="nowrap" align="flex-start" gap="sm">
                        <Box c="dimmed" style={{ flexShrink: 0, marginTop: 2 }}>
                          <TbMapPin size={18} />
                        </Box>
                        <div>
                          <Text size="sm" fw={600}>{t("storefront.location")}</Text>
                          <Text size="sm" c="dimmed">
                            {sink.region}, {getCountryName(sink.country)}
                          </Text>
                        </div>
                      </Group>
                    </Grid.Col>
                  )}
                  {market.methodology && (
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Group wrap="nowrap" align="flex-start" gap="sm">
                        <Box c="dimmed" style={{ flexShrink: 0, marginTop: 2 }}>
                          <TbTree size={18} />
                        </Box>
                        <div>
                          <Text size="sm" fw={600}>{t("storefront.methodology")}</Text>
                          <Text size="sm" c="dimmed">{market.methodology}</Text>
                        </div>
                      </Group>
                    </Grid.Col>
                  )}
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Group wrap="nowrap" align="flex-start" gap="sm">
                      <Box c="dimmed" style={{ flexShrink: 0, marginTop: 2 }}>
                        <TbLeaf size={18} />
                      </Box>
                      <div>
                        <Text size="sm" fw={600}>{t("storefront.vintage")}</Text>
                        <Text size="sm" c="dimmed">{market.vintage}</Text>
                      </div>
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Group wrap="nowrap" align="flex-start" gap="sm">
                      <Box c="dimmed" style={{ flexShrink: 0, marginTop: 2 }}>
                        <TbExternalLink size={18} />
                      </Box>
                      <div>
                        <Text size="sm" fw={600}>{t("storefront.registryRecord")}</Text>
                        <Anchor
                          href={`${REGISTRY_URL}/sink/${market.projectRefId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          fw={500}
                        >
                          {t("storefront.viewDetails")}
                        </Anchor>
                      </div>
                    </Group>
                  </Grid.Col>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Text size="lg" fw={700} mb="sm">
                  {t("storefront.impactTitle")}
                </Text>
                <Text size="sm" c="dimmed" lh={1.7}>
                  {t("storefront.impactDesc")}
                </Text>
              </Box>
            </Stack>
          </Grid.Col>

          {/* Buy box — sticky */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <MarketBuyBox
              market={market}
              sink={sink}
              isAuthenticated={isAuthenticated}
              vintages={vintages}
            />
          </Grid.Col>
        </Grid>

        {/* Price history chart below */}
        {bars.length > 0 && (
          <>
            <Divider my={48} />
            <Box>
              <Text size="lg" fw={700} mb="lg">
                {t("storefront.priceHistory")}
              </Text>
              <PriceHistoryChart bars={bars} />
            </Box>
          </>
        )}
      </Container>
    </>
  )
}
